// Adapter compatible con Supabase para migración a MySQL / Backend API REST
import { api } from './api';

type TableName =
  | 'erp_clients'
  | 'erp_products'
  | 'erp_subscriptions'
  | 'erp_invoices'
  | 'erp_contacts'
  | 'erp_payments';

class QueryBuilder<T = any> implements PromiseLike<{ data: T | null; error: any }> {
  private tableName: TableName;
  private action: 'select' | 'insert' | 'update' | 'delete' = 'select';
  private payload: any = null;
  private filters: Array<(row: any) => boolean> = [];
  private orderFn: ((a: any, b: any) => number) | null = null;
  private limitCount: number | null = null;
  private rangeStart: number | null = null;
  private rangeEnd: number | null = null;
  private isSingle = false;
  private isMaybeSingle = false;
  private targetId: number | string | null = null;

  constructor(tableName: TableName) {
    this.tableName = tableName;
  }

  select(fields?: string) {
    this.action = 'select';
    return this;
  }

  insert(data: any) {
    this.action = 'insert';
    this.payload = data;
    return this;
  }

  update(data: any) {
    this.action = 'update';
    this.payload = data;
    return this;
  }

  delete() {
    this.action = 'delete';
    return this;
  }

  eq(column: string, value: any) {
    if (column === 'id') {
      this.targetId = value;
    }
    this.filters.push((row) => String(row[column]) === String(value));
    return this;
  }

  neq(column: string, value: any) {
    this.filters.push((row) => String(row[column]) !== String(value));
    return this;
  }

  in(column: string, values: any[]) {
    const set = new Set((values || []).map((v) => String(v)));
    this.filters.push((row) => set.has(String(row[column])));
    return this;
  }

  like(column: string, pattern: string) {
    const regex = new RegExp('^' + pattern.replace(/%/g, '.*') + '$', 'i');
    this.filters.push((row) => regex.test(String(row[column] || '')));
    return this;
  }

  not(column: string, operator: string, value: any) {
    if (value === null || operator === 'is') {
      this.filters.push((row) => row[column] !== null && row[column] !== undefined);
    } else {
      this.filters.push((row) => String(row[column]) !== String(value));
    }
    return this;
  }

  gte(column: string, value: any) {
    this.filters.push((row) => row[column] >= value);
    return this;
  }

  lte(column: string, value: any) {
    this.filters.push((row) => row[column] <= value);
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    const asc = options?.ascending !== false;
    this.orderFn = (a: any, b: any) => {
      const valA = a[column];
      const valB = b[column];
      if (valA === valB) return 0;
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;
      if (valA < valB) return asc ? -1 : 1;
      return asc ? 1 : -1;
    };
    return this;
  }

  limit(count: number) {
    this.limitCount = count;
    return this;
  }

  range(from: number, to: number) {
    this.rangeStart = from;
    this.rangeEnd = to;
    return this;
  }

  single() {
    this.isSingle = true;
    return this;
  }

  maybeSingle() {
    this.isMaybeSingle = true;
    return this;
  }

  private async execute(): Promise<{ data: any; error: any }> {
    try {
      const endpoint = this.getEndpoint();

      // INSERT
      if (this.action === 'insert') {
        if (this.tableName === 'erp_payments' && this.payload.invoice_id) {
          const res = await api.invoices.registerPayment(this.payload.invoice_id, this.payload);
          return { data: res, error: null };
        }

        const res = await api.post(endpoint, this.payload);
        return { data: res, error: null };
      }

      // UPDATE
      if (this.action === 'update') {
        const id = this.targetId;
        if (!id) {
          throw new Error('Update requiere especificar .eq("id", id)');
        }
        const res = await api.put(`${endpoint}/${id}`, this.payload);
        return { data: res, error: null };
      }

      // DELETE
      if (this.action === 'delete') {
        const id = this.targetId;
        if (!id) {
          throw new Error('Delete requiere especificar .eq("id", id)');
        }
        const res = await api.delete(`${endpoint}/${id}`);
        return { data: res, error: null };
      }

      // SELECT
      let rows: any[] = await api.get(endpoint);

      // Aplicar filtros
      for (const filter of this.filters) {
        rows = rows.filter(filter);
      }

      // Ordenar
      if (this.orderFn) {
        rows.sort(this.orderFn);
      }

      // Range / Limit
      if (this.rangeStart !== null && this.rangeEnd !== null) {
        rows = rows.slice(this.rangeStart, this.rangeEnd + 1);
      } else if (this.limitCount !== null) {
        rows = rows.slice(0, this.limitCount);
      }

      if (this.isSingle) {
        if (rows.length === 0) {
          return { data: null, error: { message: 'No se encontró ningún registro' } };
        }
        return { data: rows[0], error: null };
      }

      if (this.isMaybeSingle) {
        return { data: rows.length > 0 ? rows[0] : null, error: null };
      }

      return { data: rows, error: null };
    } catch (err: any) {
      return { data: null, error: { message: err.message || 'Error de API' } };
    }
  }

  private getEndpoint(): string {
    switch (this.tableName) {
      case 'erp_clients':
        return '/clients';
      case 'erp_products':
        return '/products';
      case 'erp_subscriptions':
        return '/subscriptions';
      case 'erp_invoices':
        return '/invoices';
      case 'erp_contacts':
        return '/contacts';
      case 'erp_payments':
        return '/payments';
      default:
        throw new Error(`Tabla no soportada: ${this.tableName}`);
    }
  }

  then<TResult1 = { data: T | null; error: any }, TResult2 = never>(
    onfulfilled?: ((value: { data: T | null; error: any }) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

export const supabase = {
  from: <T = any>(table: TableName) => new QueryBuilder<T>(table),
  auth: {
    getSession: async () => {
      const token = localStorage.getItem('crm_wialon_token');
      return {
        data: {
          session: token ? { access_token: token } : null,
        },
      };
    },
  },
};
